import React from "react";
import { X, AlertTriangle, CheckCircle, FileText } from "lucide-react";
import { type SmartPasteResult } from "../../lib/smartPaste";

interface Props {
  result: SmartPasteResult;
  onUseProcessed: () => void;
  onUseOriginal: () => void;
  onCancel: () => void;
}

export const SmartPasteModal: React.FC<Props> = ({
  result,
  onUseProcessed,
  onUseOriginal,
  onCancel,
}) => {
  const formatLabels: Record<string, string> = {
    chordpro: "ChordPro",
    cifraclub: "Cifraclub",
    ug: "Ultimate Guitar",
    unknown: "Desconocido",
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E8E0D4] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#7B2335]" />
            <h3 className="text-sm font-semibold text-[#1F1F1F]">Pegar inteligente</h3>
          </div>
          <button
            onClick={onCancel}
            className="w-7 h-7 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#8A8078] hover:text-[#1F1F1F] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Formato detectado */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8078]">
              Formato detectado
            </span>
            <span
              className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                result.format === "unknown"
                  ? "bg-[#F5F0E8] text-[#78716C]"
                  : "bg-[#FDE68A] text-[#92400E]"
              }`}
            >
              {formatLabels[result.format] || result.format}
            </span>
          </div>

          {/* Advertencia si no es válido */}
          {!result.isValid && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-[#FECACA]/30 border border-[#FECACA]">
              <AlertTriangle className="w-4 h-4 text-[#991B1B] shrink-0 mt-0.5" />
              <p className="text-xs text-[#991B1B] font-medium">
                No se detectó una canción válida. El texto no contiene acordes ni letra reconocible.
              </p>
            </div>
          )}

          {/* Líneas eliminadas */}
          {result.removedLines.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8078] mb-1.5">
                Basura eliminada ({result.removedLines.length} líneas)
              </div>
              <div className="p-2.5 rounded-lg bg-[#F5F0E8] text-[11px] text-[#78716C] font-mono space-y-0.5 max-h-24 overflow-y-auto">
                {result.removedLines.map((line, i) => (
                  <div key={i} className="line-through opacity-60">
                    {line || "(vacía)"}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview procesado */}
          {result.isValid && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8078]">
                  Vista previa
                </span>
              </div>
              <div className="p-3 rounded-lg bg-[#F5F0E8] text-xs text-[#1F1F1F] font-mono leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap">
                {result.processed}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-[#E8E0D4] flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-lg text-xs font-medium text-[#78716C] hover:bg-[#F5F0E8] transition-colors"
          >
            Cancelar
          </button>
          <div className="flex-1" />
          <button
            onClick={onUseOriginal}
            className="px-3 py-2 rounded-lg text-xs font-medium text-[#5A524A] bg-[#F5F0E8] hover:bg-[#E8E0D4] transition-colors"
          >
            Usar original
          </button>
          <button
            onClick={onUseProcessed}
            disabled={!result.isValid}
            className="px-3 py-2 rounded-lg text-xs font-medium text-white bg-[#7B2335] hover:bg-[#5A1A28] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Usar procesado
          </button>
        </div>
      </div>
    </div>
  );
};
